import {
  Component, ChangeDetectionStrategy, inject, signal, OnInit, OnDestroy,
  ViewChildren, QueryList, ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';
import { FirmaService } from '../../firma-yonetim/services/firma.service';
import { AdminService } from '../../admin/services/admin.service';
import { FikoYTRequestService } from '@/src/app/shared/services/fikoyt-request.service';
import { setCihazKimlik, setCihazDurum } from '../../../guards/cihaz.guard';
import { CihazAktivasyonSocketService } from '../services/cihaz-aktivasyon-socket.service';

type Asama = 'hosgeldiniz' | 'kaydediliyor' | 'aktivasyon-bekliyor' | 'kod-girisi' | 'dogrulaniyor' | 'basarili' | 'hata';

const CIHAZ_KAYIT_KEY = 'fikoyt_hmi_kayit';

@Component({
  selector: 'hmi-aktivasyon',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './aktivasyon.component.html',
  styleUrl: './aktivasyon.component.less',
})
export class HmiAktivasyonComponent extends FikoYTKokBilesenComponent implements OnInit, OnDestroy {
  @ViewChildren('haneInput') haneInputs!: QueryList<ElementRef<HTMLInputElement>>;

  private firmaService = inject(FirmaService);
  private adminService = inject(AdminService);
  private req = inject(FikoYTRequestService);
  private aktivasyonSocket = inject(CihazAktivasyonSocketService);

  constructor() { super(); }

  asama = signal<Asama>('hosgeldiniz');
  kodHaneler: string[] = ['', '', '', '', '', ''];
  aktifHane = signal(0);
  hataMesaji = signal('');

  aktivFirmaAdi = signal('');
  aktivCihazAdi = signal('');
  aktivMod = signal('');
  aktivKonum = signal('');

  geriSayim = signal(5);
  pollingCount = signal(0);
  private geriSayimTimer: any;
  private pollingTimer: any;
  private socketSub: Subscription | null = null;

  cihazKimlik = this.getCihazKimlik();

  async ngOnInit() {
    // Zaten aktif/askida cihaz kaydı varsa → doğrudan HMI pano'ya yönlendir
    const mevcutKimlik = localStorage.getItem('fikoyt_cihaz_kimlik');
    const mevcutConfig = localStorage.getItem('fikoyt_cihaz_konfig');
    if (mevcutKimlik && mevcutConfig) {
      this.router.navigate(['/hmi-pano/pano']);
      return;
    }

    // Sadece kimlik varsa ama config yoksa da kurtarmayı dene
    if (mevcutKimlik) {
      const mevcutDurum = localStorage.getItem('fikoyt_cihaz_durum');
      if (mevcutDurum === 'aktif' || mevcutDurum === 'askida') {
        this.router.navigate(['/hmi-pano/pano']);
        return;
      }
    }

    // HW kimlik var ama cihaz kaydı silinmiş olabilir (eski askida/iptal kodu temizlemiş)
    // DB'den kontrol et, aktif/askıdaysa otomatik kurtar — AWAIT ile bekle
    await this.otomatikKurtar();

    // Önceki kayıt var mı kontrol et
    const kayit = this.mevcutKayitOku();
    if (kayit) {
      this.asama.set('aktivasyon-bekliyor');
      this.durumKontrolBaslat();
    }
  }

  /** HW kimliği DB'de aktif/askıda varsa localStorage'ı kurtarıp pano'ya yönlendir */
  private async otomatikKurtar() {
    try {
      const hwKimlik = this.cihazKimlik;
      if (!hwKimlik) return;

      console.log('[Aktivasyon] otomatikKurtar baslatildi, hwKimlik:', hwKimlik);

      const { FikoYTIstekKok } = await import('@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service');
      const istek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.aggregate',
      });
      // Basit sorgu — $lookup/$unwind kullanma (backend uyumsuzluğu riski)
      istek.parametreler = {
        aggregate: [
          { $match: { vt: 'FikoYT.sistem.cihazlar' } },
          { $match: { cihazKimlik: hwKimlik, aktivasyonDurumu: { $in: ['aktif', 'askida'] } } },
          { $sort: { _id: -1 } },
          { $limit: 1 },
        ],
      };
      await istek.isteSync();
      const sonuc = istek.cevap?.sonuc;
      const cihaz = Array.isArray(sonuc) ? sonuc[0] : null;

      console.log('[Aktivasyon] otomatikKurtar sonucu:', cihaz ? 'bulundu' : 'bulunamadi', cihaz?.aktivasyonDurumu);

      if (cihaz) {
        console.log('[Aktivasyon] Cihaz DB\'de bulundu, otomatik kurtarılıyor:', cihaz.aktivasyonDurumu);
        // localStorage'ı geri doldur
        setCihazKimlik(hwKimlik);
        setCihazDurum(cihaz.aktivasyonDurumu || 'aktif');
        localStorage.setItem('fikoyt_cihaz_konfig', JSON.stringify({
          _id: cihaz._id,
          firmaId: cihaz.firmaId,
          uygulamaModu: cihaz.uygulamaModu,
          cihazAdi: cihaz.cihazAdi,
          konum: cihaz.konum,
          kartOkuyucu: cihaz.kartOkuyucu,
          konfigurasyon: cihaz.konfigurasyon,
        }));
        localStorage.removeItem(CIHAZ_KAYIT_KEY);
        // Pano'ya yönlendir — guard artık geçirecek
        this.router.navigate(['/hmi-pano/pano']);
      }
    } catch (e) {
      console.warn('[Aktivasyon] Otomatik kurtarma hatası:', e);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    clearInterval(this.geriSayimTimer);
    clearInterval(this.pollingTimer);
    this.socketSub?.unsubscribe();
  }

  // ════════ SELF-REGISTRATION ════════

  async cihazKaydet() {
    this.asama.set('kaydediliyor');
    try {
      const mevcutKontrol = await this.adminService.aggregate('FikoYT.sistem.DB.hareketler.aggregate', [
        { $match: { vt: 'FikoYT.sistem.cihazlar' } },
        { $match: { cihazKimlik: this.cihazKimlik } },
        { $sort: { _id: -1 } },
        { $limit: 1 },
      ]);
      if (mevcutKontrol?.length > 0) {
        const mevcut = mevcutKontrol[0];
        if (mevcut.aktivasyonDurumu === 'iptal') {
          await this.req.guncelle('FikoYT.sistem.DB.hareketler.guncelle',
            { _id: mevcut._id },
            { $set: { aktivasyonDurumu: 'bekliyor', cevrimiciMi: true, olusturulmaTarihi: new Date().toISOString() } },
          );
          this.aktivasyonSocket.bildir({
            tip: 'aktivasyon_yeni_kayit',
            cihaz_kimlik: this.cihazKimlik,
            cihaz_adi: mevcut.cihazAdi || this.cihazKimlik,
            durum: 'bekliyor',
          });
        }
        localStorage.setItem(CIHAZ_KAYIT_KEY, JSON.stringify({
          cihazKimlik: this.cihazKimlik,
          kayitZamani: new Date().toISOString(),
        }));
        this.asama.set('aktivasyon-bekliyor');
        this.durumKontrolBaslat();
        return;
      }

      // DB'ye kaydet
      await this.adminService.iotOlayKaydet({
        tip: 'cihaz_kayit_istegi',
        cihazKimlik: this.cihazKimlik,
        detay: {
          userAgent: navigator.userAgent.slice(0, 100),
          ekran: `${screen.width}x${screen.height}`,
        },
      });

      // hareketler'e bekleyen kayıt oluştur
      const { FikoYTIstekKok } = await import('@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service');
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.ekle' });
      const fd = new FormData();
      fd.append('req_body', JSON.stringify({
        vt: 'FikoYT.sistem.cihazlar',
        cihazKimlik: this.cihazKimlik,
        cihazAdi: this.cihazKimlik,
        aktivasyonDurumu: 'bekliyor',
        uygulamaModu: 'pdks',
        firmaId: '',
        cevrimiciMi: true,
        donanim: {
          isletimSistemi: navigator.platform,
          ekranBoyutu: `${screen.width}x${screen.height}`,
        },
        konfigurasyon: {},
        olusturulmaTarihi: new Date().toISOString(),
      }));
      istek.parametreler = fd;
      await istek.isteSync();

      // localStorage'a kaydet
      localStorage.setItem(CIHAZ_KAYIT_KEY, JSON.stringify({
        cihazKimlik: this.cihazKimlik,
        kayitZamani: new Date().toISOString(),
      }));

      // Socket.IO ile admin panele bildir
      this.aktivasyonSocket.bildir({
        tip: 'aktivasyon_yeni_kayit',
        cihaz_kimlik: this.cihazKimlik,
        cihaz_adi: this.cihazKimlik,
        durum: 'bekliyor',
      });

      this.asama.set('aktivasyon-bekliyor');
      this.durumKontrolBaslat();
    } catch (e) {
      console.error('[HMI] Kayıt hatası:', e);
      this.hataMesaji.set('Cihaz kaydedilemedi, lütfen tekrar deneyin');
      this.asama.set('hata');
    }
  }

  /** Socket.IO ile canlı aktivasyon takibi + 30s fallback polling */
  private durumKontrolBaslat() {
    this.pollingCount.set(0);
    clearInterval(this.pollingTimer);

    // 1) Socket.IO ile anlık dinleme
    this.socketSub?.unsubscribe();
    this.aktivasyonSocket.dinlemeBasla();
    this.socketSub = this.aktivasyonSocket
      .cihazOlaylari$(this.cihazKimlik)
      .subscribe(olay => {
        if (olay.tip === 'aktivasyon_durum_degisti') {
          if (olay.durum === 'aktif' || olay.durum === 'askida') {
            clearInterval(this.pollingTimer);
            setCihazKimlik(this.cihazKimlik);
            setCihazDurum(olay.durum);
            localStorage.removeItem(CIHAZ_KAYIT_KEY);
            this.aktivFirmaAdi.set(olay.firma_adi || 'Firma');
            this.aktivCihazAdi.set(olay.cihaz_adi || this.cihazKimlik);
            this.aktivMod.set(olay.uygulama_modu || '');
            this.aktivKonum.set(olay.konum || '');
            this.asama.set('basarili');
            this.basariliYonlendir();
          } else if (olay.durum === 'iptal') {
            // Admin iptal etti — kaydı sıfırla, hoşgeldiniz ekranına dön
            clearInterval(this.pollingTimer);
            this.socketSub?.unsubscribe();
            this.socketSub = null;
            localStorage.removeItem(CIHAZ_KAYIT_KEY);
            this.nzMessage.warning('Cihaz kaydı yönetici tarafından iptal edildi');
            this.asama.set('hosgeldiniz');
          }
        }
      });

    // 2) Fallback polling (30s) — Socket.IO bağlantısı koparsa
    this.pollingTimer = setInterval(() => {
      this.pollingCount.update(c => c + 1);
      this.durumKontrolEt();
    }, 30000);

    // İlk kontrolü hemen yap
    this.durumKontrolEt();
  }

  private async durumKontrolEt() {
    try {
      // En son kaydı al ($lookup/$unwind kullanma — backend uyumsuzluk riski)
      const sonuc = await this.adminService.aggregate('FikoYT.sistem.DB.hareketler.aggregate', [
        { $match: { vt: 'FikoYT.sistem.cihazlar' } },
        { $match: { cihazKimlik: this.cihazKimlik } },
        { $sort: { _id: -1 } },
        { $limit: 1 },
      ]);

      const cihaz = sonuc?.[0];
      if (!cihaz) return; // Henüz kayıt oluşmamış, beklemeye devam

      if (cihaz.aktivasyonDurumu === 'iptal') {
        // En son kayıt iptal edilmiş — hoşgeldiniz ekranına dön
        clearInterval(this.pollingTimer);
        this.socketSub?.unsubscribe();
        this.socketSub = null;
        localStorage.removeItem(CIHAZ_KAYIT_KEY);
        this.nzMessage.warning('Cihaz kaydı yönetici tarafından iptal edildi');
        this.asama.set('hosgeldiniz');
        return;
      }

      if (cihaz.aktivasyonDurumu === 'aktif' || cihaz.aktivasyonDurumu === 'askida') {
        clearInterval(this.pollingTimer);
        // Aktif veya askida — localStorage'a yaz ve yönlendir (shell overlay halleder)
        setCihazKimlik(this.cihazKimlik);
        setCihazDurum(cihaz.aktivasyonDurumu);
        localStorage.removeItem(CIHAZ_KAYIT_KEY);

        this.aktivFirmaAdi.set(cihaz.firmaAdi || 'Firma');
        this.aktivCihazAdi.set(cihaz.cihazAdi || this.cihazKimlik);
        this.aktivMod.set(cihaz.uygulamaModu || '');
        this.aktivKonum.set(cihaz.konum || '');
        this.asama.set('basarili');
        this.basariliYonlendir();
      }
    } catch {}
  }

  kaydiIptalEt() {
    clearInterval(this.pollingTimer);
    localStorage.removeItem(CIHAZ_KAYIT_KEY);
    this.asama.set('hosgeldiniz');
  }

  // ════════ KOD GİRİŞ (ALTERNATİF) ════════

  kodTam(): boolean {
    return this.kodHaneler.every(h => h.length === 1);
  }

  haneGirildi(event: Event, idx: number) {
    const input = event.target as HTMLInputElement;
    let val = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (val.length > 1) val = val.charAt(val.length - 1);
    this.kodHaneler[idx] = val;
    this.hataMesaji.set('');
    if (val && idx < 5) {
      this.aktifHane.set(idx + 1);
      setTimeout(() => { this.haneInputs?.toArray()?.[idx + 1]?.nativeElement?.focus(); });
    }
  }

  tusKontrol(event: KeyboardEvent, idx: number) {
    if (event.key === 'Backspace') {
      if (!this.kodHaneler[idx] && idx > 0) {
        this.kodHaneler[idx - 1] = '';
        this.aktifHane.set(idx - 1);
        setTimeout(() => { this.haneInputs?.toArray()?.[idx - 1]?.nativeElement?.focus(); });
      } else { this.kodHaneler[idx] = ''; }
    } else if (event.key === 'Enter' && this.kodTam()) {
      this.kodDogrula();
    } else if (event.key === 'ArrowLeft' && idx > 0) {
      this.aktifHane.set(idx - 1);
      this.haneInputs?.toArray()?.[idx - 1]?.nativeElement?.focus();
    } else if (event.key === 'ArrowRight' && idx < 5) {
      this.aktifHane.set(idx + 1);
      this.haneInputs?.toArray()?.[idx + 1]?.nativeElement?.focus();
    }
  }

  yapistir(event: ClipboardEvent) {
    event.preventDefault();
    const text = (event.clipboardData?.getData('text') || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (text.length < 6) return;
    for (let i = 0; i < 6; i++) this.kodHaneler[i] = text.charAt(i);
    this.aktifHane.set(5);
    setTimeout(() => { this.haneInputs?.toArray()?.[5]?.nativeElement?.focus(); });
  }

  async kodDogrula() {
    if (!this.kodTam()) return;
    const kod = this.kodHaneler.join('');
    this.asama.set('dogrulaniyor');
    try {
      const sonuc = await this.firmaService.cihazAktivasyonDogrula(kod, this.cihazKimlik);
      if (!sonuc) {
        this.asama.set('hata');
        this.hataMesaji.set('Geçersiz veya süresi dolmuş aktivasyon kodu');
        return;
      }
      setCihazKimlik(this.cihazKimlik);
      localStorage.removeItem(CIHAZ_KAYIT_KEY);
      this.aktivFirmaAdi.set(sonuc.firma?.firmaAdi || 'Firma');
      this.aktivCihazAdi.set(sonuc.cihazAdi || '');
      this.aktivMod.set(sonuc.uygulamaModu || '');
      this.aktivKonum.set(sonuc.konum || '');
      this.asama.set('basarili');
      this.basariliYonlendir();
    } catch {
      this.asama.set('hata');
      this.hataMesaji.set('Sunucu hatası — lütfen tekrar deneyin');
    }
  }

  sifirla() {
    this.kodHaneler = ['', '', '', '', '', ''];
    this.aktifHane.set(0);
    this.hataMesaji.set('');
    this.asama.set('hosgeldiniz');
    clearInterval(this.geriSayimTimer);
  }

  geriDon() {
    this.router.navigate(['/']);
  }

  // ════════ YARDIMCI ════════

  private basariliYonlendir() {
    this.geriSayim.set(5);
    this.geriSayimTimer = setInterval(() => {
      const kalan = this.geriSayim() - 1;
      this.geriSayim.set(kalan);
      if (kalan <= 0) {
        clearInterval(this.geriSayimTimer);
        this.router.navigate(['/hmi-pano/pano']);
      }
    }, 1000);
  }

  private mevcutKayitOku(): { cihazKimlik: string; kayitZamani: string } | null {
    try {
      const raw = localStorage.getItem(CIHAZ_KAYIT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  private getCihazKimlik(): string {
    const storageKey = 'fikoyt_cihaz_hw_kimlik';
    let kimlik = localStorage.getItem(storageKey);
    if (kimlik) return kimlik;
    const parts = [
      navigator.userAgent.slice(0, 20),
      screen.width, screen.height, screen.colorDepth,
      new Date().getTime().toString(36),
      Math.random().toString(36).slice(2, 8),
    ];
    kimlik = 'HMI-' + this.hashCode(parts.join('|')).toString(16).toUpperCase();
    localStorage.setItem(storageKey, kimlik);
    return kimlik;
  }

  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const chr = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr;
      hash |= 0;
    }
    return Math.abs(hash);
  }
}
